import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    IconButton,
    Modal,
    Paper,
    Snackbar,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    CircularProgress,
    TextField,
    InputAdornment
} from "@mui/material";
import Table from "../../../components/Table/Table";
import { useGetTenancyBasicsQuery, useGetTenancyByIdQuery, useUpdateAutoSyncMutation, useUpdateSyncFilteringMutation } from "../../../store/services/tenancy.service";
import { useGetUsersFromGroupQuery } from "../../../store/services/user-service";
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import { useTheme } from "@mui/material/styles";
import GroupUsersTable from "../../users/GroupUsersTable";
import { SearchOutlined } from "@mui/icons-material";
import { getCardColor } from "../../../utils/colorUtils";
import ConfirmationDialog from "../../../utils/confirmationDialog";


GroupTable.propTypes = {
    type: PropTypes.string.isRequired,
    groupIds: PropTypes.array,
    setGroupId: PropTypes.func
};

export default function GroupTable({ type, setGroupId, groupIds }) {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [selected, setSelected] = useState([]);
    const [editGroups, setEditGroups] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [originalGroups, setOriginalGroups] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [filterEnabled, setFilterEnabled] = useState(false);

    // Confirmation dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState("");
    const [dialogAction, setDialogAction] = useState(null);
    const [dialogCancelAction, setDialogCancelAction] = useState(null);

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });
    const [userPaginationModel, setUserPaginationModel] = useState({
        pageSize: 10,
        page: 0
    });

    const { data: tenancyData, error: tenancyError, isLoading: tenancyLoading, refetch } = useGetTenancyBasicsQuery({
        tenancyId: tenancyId
    });

    // Get full tenancy data for enableSyncFiltering field
    const { data: fullTenancyData, refetch: refetchFullTenancy } = useGetTenancyByIdQuery({
        tenancyId: tenancyId
    });

    const { data: groupData, error: groupError, isLoading: groupLoading } = useGetUsersFromGroupQuery({
        groupId: selectedGroupId,
        tenancyId: tenancyId
    }, {
        skip: !selectedGroupId
    });

    const [updateAutoSync] = useUpdateAutoSyncMutation();
    const [updateSyncFiltering, { isLoading: isSyncFilteringLoading }] = useUpdateSyncFilteringMutation();

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);

        // Perform the same action as "Edit Groups" then "Update Auto Sync"
        handleUpdateAutoSync().then(() => {
            setSnackbarSeverity("success");
            setSnackbarMessage("Refreshing users in the selected groups.");
            setSnackbarOpen(true);
        }).catch((error) => {
            console.error("Error refreshing groups:", error);
            setSnackbarSeverity("error");
            setSnackbarMessage("Failed to refresh groups.");
            setSnackbarOpen(true);
        }).finally(() => {
            setIsRefreshing(false);
        });
    }, [selectedGroups, tenancyId]); // Remove handleUpdateAutoSync from dependencies

    useEffect(() => {
        if (tenancyData && tenancyData.groups) {
            const initialSelectedGroups = tenancyData.groups.filter(group => group.autoSync);
            setSelectedGroups(initialSelectedGroups);
            setOriginalGroups(initialSelectedGroups);
        }
    }, [tenancyData]);

    // Set initial filter state from full tenancy data
    useEffect(() => {
        if (fullTenancyData) {
            console.log("Setting initial filterEnabled from fullTenancyData:", fullTenancyData.enableSyncFiltering);
            setFilterEnabled(fullTenancyData.enableSyncFiltering || false);
        }
    }, [fullTenancyData]);

    useEffect(() => {
        if (!modalOpen) {
            setUserPaginationModel({
                pageSize: 10,
                page: 0
            });
        }
    }, [modalOpen]);

    const validateAutoSync = () => {
        if (selectedGroups.length === 0) return true;
        const origin = selectedGroups[0].origin;
        return selectedGroups.every(group => group.origin === origin);
    };

    const handleUpdateAutoSync = async () => {
        setIsRefreshing(true);
        // We are assuming that the first group selected is the type of sync (Google or Office).
        var origin = "office";
        if (selectedGroups.length > 0) {
            if (selectedGroups[0].origin === "google") {
                origin = "google";
            }
        }

        const updatedGroups = selectedGroups.map(group => ({
            ...group,
            DisplayName: group.name,  // Map 'name' field to 'DisplayName' for API compatibility
            autoSync: true,  // Explicitly set autoSync to true for selected groups
            CreateMe: true,
            origin: {
                type: origin
            },
            Id: group.office365Identifier
        }));

        const body = {
            TenancyId: tenancyId,
            Users: [],
            Groups: updatedGroups,
            AutoSync: true,
            Origin: { type: origin },
            Import: true
        };

        return updateAutoSync({ tenancyId, data: body })
            .unwrap()
            .then(() => {
                setIsRefreshing(false);
            })
            .catch((error) => {
                console.error(error);
                setIsRefreshing(false);
                throw error; // Re-throw to allow the caller to handle it
            });
    };


    const handleEditGroups = () => {
        if (editGroups) {
            handleUpdateAutoSync().then(() => {
                setSnackbarSeverity("success");
                setSnackbarMessage("Updated auto sync groups.");
                setSnackbarOpen(true);
            });
        } else {
            setOriginalGroups(selectedGroups);
        }
        setEditGroups(!editGroups);
    };

    const handleCancelEdit = () => {
        setSelectedGroups(originalGroups);
        setEditGroups(false);
    };

    const handleOpenModal = (groupId) => {
        setSelectedGroupId(groupId);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedGroupId(null);
        setUserPaginationModel({
            pageSize: 10,
            page: 0
        });
    };

    const handleFilterChange = () => {
        if (!filterEnabled) {
            // User is enabling the filter - show confirmation
            setDialogTitle("Enable User Filter");
            setDialogContent(
                <>
                    Are you sure you want to enable account status rules?
                    <br />
                    <br />
                    This will automatically remove or disable users whose accounts are inactive in the connected directory during the next sync.
                    <br />
                    <br />
                    • <strong>Microsoft Entra:</strong> Removes users where accountEnabled = false.
                    <br />
                    • <strong>Google Workspace:</strong> Disables users where suspended = true and removes users where archived = true.
                    <br />
                    <br />
                    This change will take effect on your next directory sync.
                </>
            );
            setDialogCancelAction(() => () => {
                // Do nothing - keep filter disabled
            });
            setDialogAction(() => () => {
                // Enable the filter
                updateSyncFiltering({ tenancyId, enableSyncFiltering: true })
                    .unwrap()
                    .then((response) => {
                        console.log("Enable response:", response);
                        console.log("enableSyncFiltering value:", response.enableSyncFiltering);
                        // Refetch full tenancy data to get updated enableSyncFiltering value
                        refetchFullTenancy();
                        setSnackbarSeverity("success");
                        setSnackbarMessage("Sync filtering has been enabled");
                        setSnackbarOpen(true);
                    })
                    .catch((error) => {
                        console.error("Error enabling sync filtering:", error);
                        setSnackbarSeverity("error");
                        setSnackbarMessage("Failed to enable sync filtering");
                        setSnackbarOpen(true);
                    });
            });
            setDialogOpen(true);
        } else {
            // User is disabling the filter - no confirmation needed
            updateSyncFiltering({ tenancyId, enableSyncFiltering: false })
                .unwrap()
                .then((response) => {
                    // Refetch full tenancy data to get updated enableSyncFiltering value
                    refetchFullTenancy();
                    setSnackbarSeverity("success");
                    setSnackbarMessage("Sync filtering has been disabled");
                    setSnackbarOpen(true);
                })
                .catch((error) => {
                    console.error("Error disabling sync filtering:", error);
                    setSnackbarSeverity("error");
                    setSnackbarMessage("Failed to disable sync filtering");
                    setSnackbarOpen(true);
                });
        }
    };

    const getButtonBackgroundColor = (isSelected) => {
        if (isSelected) {
            return !editGroups ? theme.palette.primary.light : theme.palette.primary.main;
        }
        return !editGroups ? theme.palette.grey[100] : theme.palette.grey[200];
    };

    const getButtonColor = (isSelected) => {
        return isSelected && !editGroups ? 'white' : 'inherit';
    };

    const getButtonDisabledBackgroundColor = (isSelected) => {
        return isSelected ? theme.palette.primary.light : theme.palette.grey[100];
    };

    const getButtonDisabledColor = (isSelected) => {
        return isSelected ? 'black' : theme.palette.grey[400];
    };

    const headCells = [
        {
            id: "autoSync",
            label: "Auto Sync",
            render: (dataRow) => {
                const isSelected = selectedGroups.some(group => group.userGroupId === dataRow.userGroupId);
                return (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                            if (editGroups) {
                                let newSelectedGroups;
                                if (isSelected) {
                                    newSelectedGroups = selectedGroups.filter(group => group.userGroupId !== dataRow.userGroupId);
                                } else {
                                    newSelectedGroups = [...selectedGroups, dataRow];
                                }
                                setSelectedGroups(newSelectedGroups);
                            }
                        }}
                        disabled={!editGroups}
                        color={isSelected ? "primary" : "inherit"}
                        sx={{
                            minWidth: 100,
                            '&.Mui-disabled': {
                                color: `${getButtonDisabledColor(isSelected)}!important`,
                            },
                            '&:hover': {
                                backgroundColor: (!isSelected && editGroups) ? lightMode ? theme.palette.grey[100] : theme.palette.black.light : undefined,
                            },
                        }}
                        /*
                        sx={{
                            minWidth: 100,
                            backgroundColor: getButtonBackgroundColor(isSelected),
                            color: getButtonColor(isSelected),
                            '&.Mui-disabled': {
                                backgroundColor: `${getButtonDisabledBackgroundColor(isSelected)}!important`,
                                color: `${getButtonDisabledColor(isSelected)}!important`,
                            },
                            '&:hover': {
                                backgroundColor: !isSelected && editGroups ? theme.palette.grey[50] : undefined,
                            },
                        }}
                        */

                    >
                        {isSelected ? "ON" : "OFF"}
                    </Button>
                );
            },
            initialVisible: type === "edit"
        },
        {
            id: "select",
            label: "Select",
            render: (dataRow) => {
                const isSelected = groupIds?.map(group => group.userGroupId).includes(dataRow.userGroupId);
                return (
                    <IconButton
                        size="small"
                        onClick={() => {
                            if (isSelected) {
                                setGroupId(groupIds.filter(group => group.userGroupId !== dataRow.userGroupId));
                            } else {
                                setGroupId([...groupIds, dataRow]);
                            }
                        }}
                    >
                        {isSelected ? <CheckBoxRoundedIcon color="warning" /> : <CheckBoxOutlineBlankRoundedIcon color={lightMode ? "black" : "white"} />}
                    </IconButton>
                );
            },
            initialVisible: type === "select"
        },
        {
            id: "viewUsers",
            label: "View users",
            render: (dataRow) => (
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleOpenModal(dataRow.userGroupId)}
                    color="warning"
                >
                    View users
                </Button>
            ),
            initialVisible: true
        },
        { id: "name", label: "Name", initialVisible: true },
        { id: "origin", label: "Origin", initialVisible: true }

    ];

    const toolbarActions = [
        {
            render: () => (
                <Box>
                    {type === "edit" && (
                        <>
                            {selectedGroups.length > 0 && (
                                <Button
                                    size="medium"
                                    variant="contained"
                                    onClick={handleRefresh}
                                    sx={{ mr: 1 }}
                                    color='warning'
                                    disabled={editGroups || isRefreshing}
                                >
                                    {isRefreshing ? <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} /> : "Resync"}
                                </Button>
                            )}
                            <Button
                                size="medium"
                                variant="contained"
                                sx={{ textTransform: "capitalize", mr: 1 }}
                                aria-haspopup="true"
                                onClick={handleEditGroups}
                            >
                                {editGroups ? "Update Auto Sync" : "Edit Groups"}
                            </Button>
                            {editGroups && (
                                <Button
                                    size="medium"
                                    variant="outlined"
                                    sx={{ textTransform: "capitalize" }}
                                    aria-haspopup="true"
                                    onClick={handleCancelEdit}
                                >
                                    Cancel
                                </Button>
                            )}
                        </>
                    )}
                </Box>
            )
        }
    ];

    /* ------------------- OLD GROUP USERS COMPONENT -------------------

    const UserTable = () => {
        const [searchTerm, setSearchTerm] = useState('');

        const handleChangePage = (event, newPage) => {
            setUserPaginationModel(prev => ({ ...prev, page: newPage }));
        };

        const handleChangeRowsPerPage = (event) => {
            setUserPaginationModel({
                page: 0,
                pageSize: parseInt(event.target.value, 10)
            });
        };

        const filteredData = useMemo(() => {
            return (groupData || []).filter(user =>
                `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                new Date(user.dateCreated).toLocaleDateString().includes(searchTerm)
            );
        }, [groupData, searchTerm]);

        if (tenancyLoading || groupLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <Paper
                sx={{
                    width: '100%',
                    overflow: 'hidden',
                    display: 'grid',
                    gridTemplateRows: 'auto auto 1fr auto',
                    height: 550, // Increased height to accommodate search bar
                    boxShadow: 3,
                    borderRadius: 2
                }}
            >
                <Box sx={{ p: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)'}}>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search Users"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <TableContainer>
                    <MuiTable stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Date Created</TableCell>
                                <TableCell>Email</TableCell>
                            </TableRow>
                        </TableHead>
                    </MuiTable>
                </TableContainer>
                <TableContainer sx={{ overflowY: 'auto' }}>
                    <MuiTable>
                        <TableBody>
                            {filteredData
                                .slice(
                                    userPaginationModel.page * userPaginationModel.pageSize,
                                    userPaginationModel.page * userPaginationModel.pageSize + userPaginationModel.pageSize
                                )
                                .map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                                        <TableCell>{new Date(user.dateCreated).toLocaleDateString()}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </MuiTable>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filteredData.length}
                    page={userPaginationModel.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={userPaginationModel.pageSize}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        borderTop: '1px solid rgba(224, 224, 224, 1)',
                        '.MuiTablePagination-toolbar': {
                            height: '52px',
                        }
                    }}
                />
            </Paper>
        );
    };
    */

    return (
        <>
            <Table
                name={"group"}
                data={tenancyData && tenancyData.groups ? tenancyData.groups : []}
                error={tenancyError}
                loading={tenancyLoading}
                headCells={headCells}
                search={true}
                editColumn={false}
                checkbox={false}
                showToolbarActions={true}
                toolbarActions={toolbarActions}
                onDelete={() => console.log("Delete Groups Not Allowed")}
                selected={selected}
                setSelected={setSelected}
                maxTableHeight="calc(100vh - 400px)"
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                plural={"groups"}
                showFilterSwitch={true}
                filterEnabled={filterEnabled}
                onFilterChange={handleFilterChange}
                filterLoading={isSyncFilteringLoading}
                filterTooltip={
                    <>
                        Automatically removes or disables users whose accounts are inactive in the connected directory.
                        <br />
                        <br />
                        • <strong>Microsoft Entra:</strong> Removes users where accountEnabled = false.
                        <br />
                        • <strong>Google Workspace:</strong> Disables users where suspended = true and removes users where archived = true.
                    </>
                }
            />
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 900,
                    bgcolor: theme.palette.background.default,
                    boxShadow: 24,
                    borderRadius: 2,
                    p: 4,

                }}>
                    <Typography id="modal-modal-title" variant="h4" component="h2" gutterBottom>
                        Group Users
                    </Typography>
                    <GroupUsersTable
                        selectedGroupId={selectedGroupId}
                        closeModal={handleCloseModal}
                    />
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mt:2 }}>
                        <Button onClick={handleCloseModal} variant="outlined" color="black">Close</Button>
                    </Box>
                </Box>
            </Modal>
            <ConfirmationDialog
                open={dialogOpen}
                title={dialogTitle}
                content={dialogContent}
                onConfirm={() => {
                    setDialogOpen(false);
                    dialogAction?.();
                }}
                onCancel={() => {
                    setDialogOpen(false);
                    dialogCancelAction?.();
                }}
            />
        </>
    );
}
